import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, CalendarClock } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateLong } from "../lib/dateFormat";
import { bumpUnreadUpdates } from "../lib/unreadUpdates";
import WeeklyReportDetailModal from "../components/customer/WeeklyReportDetailModal";

function StatusPhoto({ fileId, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch (e) { /* ignore */ }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!src) return <div className={`bg-navy/5 animate-pulse ${className}`} />;
  return <img src={src} alt="Status" className={`object-cover ${className}`} />;
}

function PhotoCarousel({ ids, de }) {
  const [idx, setIdx] = useState(0);
  if (!ids || ids.length === 0) {
    return (
      <div className="bg-ivory/60 aspect-[4/3] flex items-center justify-center label-caps">
        {de ? "Kein Foto" : "No photo"}
      </div>
    );
  }
  const total = ids.length;
  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + total) % total); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % total); };
  return (
    <div className="relative bg-navy/5" data-testid="photo-carousel">
      <StatusPhoto fileId={ids[idx]} className="w-full aspect-[4/3]" />
      {total > 1 && (
        <>
          <button
            data-testid="carousel-prev"
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-navy/80 text-ivory text-xl hover:bg-navy transition-colors"
            style={{ background: withAlpha(BRAND_COLORS.navy, 0.85) }}
            aria-label="Previous"
          >‹</button>
          <button
            data-testid="carousel-next"
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-navy/80 text-ivory text-xl hover:bg-navy transition-colors"
            style={{ background: withAlpha(BRAND_COLORS.navy, 0.85) }}
            aria-label="Next"
          >›</button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-navy text-ivory px-2 py-0.5 text-[10px] tracking-widest uppercase" style={{ background: BRAND_COLORS.navy }}>
            {idx + 1} <span className="mx-1 opacity-60">·</span> {total}
          </div>
          <div className="absolute bottom-3 right-3 flex gap-1">
            {ids.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 ${i === idx ? "bg-gold" : "bg-ivory/60"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function CustomerUpdates() {
  const { t, lang } = useI18n();
  const [updates, setUpdates] = useState(null);
  const [properties, setProperties] = useState({});
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([api.get("/weekly-updates"), api.get("/properties")]);
        const propMap = {};
        p.data.forEach((pr) => { propMap[pr.id] = pr; });
        setProperties(propMap);
        setUpdates(u.data);
      } catch (e) {
        console.error("CustomerUpdates load failed:", e);
      }
    })();
  }, []);

  const photoIds = (u) => {
    if (u.photo_file_ids && u.photo_file_ids.length > 0) return u.photo_file_ids;
    if (u.photo_file_id) return [u.photo_file_id];
    return [];
  };

  // Feb 2026 · User-Directive „Klick auf Bericht öffnet Detailansicht +
  // markiert als gelesen + Bell dekrementiert" (5 Sep 2026). Optimistisch
  // im lokalen State (setzt `read: true`) + POST /mark-read + Bump-Event
  // fuer `useUnreadUpdates` im Header. Bei Serverfehler bleibt der Modal
  // trotzdem offen — der naechste Poll setzt den Status ggf. wieder auf
  // unread, wodurch der Fehler von selbst visualisiert wird.
  const openReport = async (u) => {
    setOpenId(u.id);
    if (u.read) return;
    setUpdates((prev) => (prev || []).map((r) => (r.id === u.id ? { ...r, read: true } : r)));
    try {
      await api.post(`/weekly-updates/${u.id}/mark-read`);
      bumpUnreadUpdates();
    } catch (e) {
      console.error("mark-read failed:", e);
    }
  };

  const openUpdate = openId ? (updates || []).find((u) => u.id === openId) : null;

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-updates">
      <div className="mb-10">
        <div className="label-caps mb-3">Weissblom</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">{t.updates.title}</h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">{t.updates.lead}</p>
      </div>

      {updates === null ? (
        <div className="bg-white border border-navy/10 p-12 text-center label-caps">{t.common.loading}</div>
      ) : updates.length === 0 ? (
        <div className="bg-white border border-dashed border-navy/20 py-20 text-center text-navy-dark/50 text-[13px]">
          {t.updates.empty}
        </div>
      ) : (
        <div className="space-y-8" data-testid="customer-updates-list">
          {updates.map((u) => {
            // 27 Sep 2026 · Verlängerungs-Neuigkeit (goldenes Abzeichen) —
            // neutrale, kompakte Karte statt Wochenbericht-Layout (kein Foto/PDF).
            if (u.type === "package_renewal") {
              const unread = u.read !== true;
              const to = u.recommended_package_id
                ? `/app/service-packages?package=${u.recommended_package_id}`
                : "/app/service-packages";
              const markRead = () => {
                if (u.read === true) return;
                setUpdates((prev) => (prev || []).map((r) => (r.id === u.id ? { ...r, read: true } : r)));
                api.post(`/weekly-updates/${u.id}/mark-read`).then(bumpUnreadUpdates).catch(() => {});
              };
              return (
                <Link
                  key={u.id}
                  to={to}
                  onClick={markRead}
                  data-testid={`renewal-update-card-${u.id}`}
                  data-unread={unread ? "1" : "0"}
                  className="block bg-white border p-8 md:p-10 hover:shadow-lg transition-shadow"
                  style={{
                    borderColor: unread ? withAlpha(BRAND_COLORS.gold, 0.6) : withAlpha(BRAND_COLORS.navy, 0.1),
                    boxShadow: unread ? `inset 4px 0 0 ${BRAND_COLORS.gold}` : "none",
                  }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {unread && (
                      <span
                        data-testid={`renewal-update-unread-dot-${u.id}`}
                        className="w-2 h-2 rounded-full inline-block"
                        style={{ background: BRAND_COLORS.gold }}
                      />
                    )}
                    <CalendarClock className="w-4 h-4 text-gold" strokeWidth={1.8} />
                    <div className="label-caps">{lang === "de" ? "Verlängerung Ihres Service-Pakets" : "Service package renewal"}</div>
                  </div>
                  <h2 className={`font-display text-2xl md:text-3xl text-navy mb-3 ${unread ? "font-bold" : ""}`}>
                    {u.package_title}{lang === "de" ? " läuft bald aus" : " ending soon"}
                  </h2>
                  <p className="text-[14px] text-navy-dark/80 leading-relaxed">{u.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-gold font-medium text-[12px] tracking-[0.14em] uppercase">
                    {lang === "de" ? "Jetzt verlängern" : "Renew now"}
                    <span aria-hidden="true">→</span>
                  </span>
                </Link>
              );
            }
            const prop = properties[u.property_id];
            const unread = u.read !== true;
            return (
              <article
                key={u.id}
                data-testid={`weekly-report-card-${u.id}`}
                data-unread={unread ? "1" : "0"}
                className="bg-white border grid grid-cols-1 md:grid-cols-2 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                style={{
                  borderColor: unread ? withAlpha(BRAND_COLORS.gold, 0.6) : withAlpha(BRAND_COLORS.navy, 0.1),
                  boxShadow: unread ? `inset 4px 0 0 ${BRAND_COLORS.gold}` : "none",
                }}
                onClick={() => openReport(u)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openReport(u);
                  }
                }}
              >
                <PhotoCarousel ids={photoIds(u)} de={lang === "de"} />
                <div className="p-8 md:p-10 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    {unread && (
                      <span
                        data-testid={`weekly-report-unread-dot-${u.id}`}
                        className="w-2 h-2 rounded-full inline-block"
                        style={{ background: BRAND_COLORS.gold }}
                        aria-label={lang === "de" ? "Ungelesen" : "Unread"}
                      />
                    )}
                    <div className="label-caps">
                      {t.updates.week_of} {deDateLong(u.week_of)}
                    </div>
                  </div>
                  {prop && (
                    <h2 className={`font-display text-3xl text-navy mb-4 ${unread ? "font-bold" : ""}`}>
                      {prop.name}
                    </h2>
                  )}
                  <p className={`text-[14px] leading-relaxed whitespace-pre-wrap flex-1 ${unread ? "text-navy-dark font-medium" : "text-navy-dark/80"}`}>
                    {u.summary}
                  </p>
                  {u.team_note && (
                    <div className="mt-5 pt-4 border-t border-navy/10">
                      <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>
                        {lang === "de" ? "Persönliches Fazit" : "Personal note"}
                      </div>
                      <p className="text-[13px] text-navy-dark/75 italic leading-relaxed whitespace-pre-wrap">{u.team_note}</p>
                    </div>
                  )}
                  <div className="mt-6 pt-4 border-t border-navy/10 flex items-center justify-between gap-3 flex-wrap">
                    <div className="label-caps">Weissblom Team</div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/weekly-updates/${u.id}/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`weekly-pdf-btn-${u.id}`}
                      className="inline-flex items-center gap-1.5 border border-navy/25 hover:border-gold text-navy px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase transition-colors"
                    >
                      <FileDown className="w-3.5 h-3.5" strokeWidth={1.6} /> {lang === "de" ? "Als PDF" : "As PDF"}
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {openUpdate && (
        <WeeklyReportDetailModal
          update={openUpdate}
          property={properties[openUpdate.property_id]}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}
